/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { GripVertical, X, Check, Save } from 'lucide-react';
import { adminApi } from '../../lib/api';

const BestSellersTab = () => {
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [initialIds, setInitialIds] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [ps, cur] = await Promise.all([
          adminApi.listProducts(),
          adminApi.getBestSellers(),
        ]);
        setProducts(ps || []);
        setSelectedIds(Array.isArray(cur?.product_ids) ? cur.product_ids : []);
        setInitialIds(Array.isArray(cur?.product_ids) ? cur.product_ids : []);
      } catch (e) {
        setErr('Failed to load products or best sellers');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const productById = useMemo(() => {
    const m = new Map();
    products.forEach((p) => m.set(p.id, p));
    return m;
  }, [products]);

  const availableFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products
      .filter((p) => !selectedIds.includes(p.id))
      .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.shade || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q));
  }, [products, selectedIds, query]);

  const dirty = useMemo(() => {
    if (selectedIds.length !== initialIds.length) return true;
    return selectedIds.some((id, i) => id !== initialIds[i]);
  }, [selectedIds, initialIds]);

  const add = (id) => {
    if (selectedIds.includes(id)) return;
    if (selectedIds.length >= 20) {
      setErr('Maximum 20 best-sellers allowed');
      return;
    }
    setErr('');
    setSelectedIds([...selectedIds, id]);
  };

  const remove = (id) => {
    setSelectedIds(selectedIds.filter((x) => x !== id));
  };

  const move = (index, dir) => {
    const next = [...selectedIds];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setSelectedIds(next);
  };

  const save = async () => {
    setSaving(true);
    setErr('');
    setMsg('');
    try {
      const res = await adminApi.setBestSellers(selectedIds);
      const saved = res?.product_ids || selectedIds;
      setInitialIds(saved);
      setSelectedIds(saved);
      setMsg('Best sellers updated.');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="mt-8 text-[#7a6a5a]">Loading…</div>;
  }

  return (
    <div className="mt-8" data-testid="best-sellers-tab">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[11px] tracking-[0.28em] text-[#3a2e26]">HOMEPAGE FEATURED EDIT</div>
          <p className="mt-2 text-sm text-[#5a4a3e] max-w-xl">
            Pick and order the products that appear in the Best Sellers slider on the homepage. Up to 20.
          </p>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="best-sellers-save-btn"
          className="inline-flex items-center gap-2 bg-[#1a1410] text-[#f5f1e6] px-6 py-3 text-[11px] tracking-[0.32em] disabled:opacity-40"
        >
          <Save className="w-4 h-4" strokeWidth={1.6} />
          {saving ? 'SAVING…' : 'SAVE CHANGES'}
        </button>
      </div>

      {err && <div className="mb-4 p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}
      {msg && <div className="mb-4 p-3 bg-[#e6efe1] border border-[#b7ceac] text-sm text-[#3d5a2a]">{msg}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Selected list */}
        <div className="border border-[#e5dfd0] p-5 bg-white/50">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] tracking-[0.28em] text-[#3a2e26]">SELECTED ({selectedIds.length}/20)</div>
          </div>
          {selectedIds.length === 0 ? (
            <div className="text-sm text-[#7a6a5a] py-8 text-center">No best sellers selected yet.</div>
          ) : (
            <ul className="divide-y divide-[#e5dfd0]">
              {selectedIds.map((id, i) => {
                const p = productById.get(id);
                if (!p) return (
                  <li key={id} className="py-3 flex items-center justify-between text-sm text-[#7a3a2a]">
                    <span>Missing product: {id}</span>
                    <button onClick={() => remove(id)} className="text-[10px] tracking-[0.22em] border-b border-[#7a3a2a]">REMOVE</button>
                  </li>
                );
                return (
                  <li key={id} data-testid={`selected-row-${id}`} className="py-3 flex items-center gap-3">
                    <div className="flex flex-col text-[#7a6a5a]">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-xs disabled:opacity-30 hover:text-[#1a1410]" aria-label="Move up">▲</button>
                      <button onClick={() => move(i, 1)} disabled={i === selectedIds.length - 1} className="text-xs disabled:opacity-30 hover:text-[#1a1410]" aria-label="Move down">▼</button>
                    </div>
                    <div className="w-12 h-12 bg-[#e9e2d3] overflow-hidden shrink-0">
                      {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-base truncate">{p.name}</div>
                      <div className="text-[10px] tracking-[0.22em] text-[#7a6a5a] uppercase">{p.shade} · {p.category}</div>
                    </div>
                    <div className="text-sm text-[#5a4a3e] whitespace-nowrap">Rs. {p.price?.toFixed?.(2) ?? p.price}</div>
                    <button
                      onClick={() => remove(id)}
                      data-testid={`remove-selected-${id}`}
                      className="p-1 text-[#7a6a5a] hover:text-[#7a3a2a]"
                      aria-label="Remove"
                    >
                      <X className="w-4 h-4" strokeWidth={1.6} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Catalogue picker */}
        <div className="border border-[#e5dfd0] p-5 bg-white/50">
          <div className="flex items-center justify-between mb-4 gap-3">
            <div className="text-[11px] tracking-[0.28em] text-[#3a2e26]">CATALOGUE</div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, shade, category…"
              data-testid="best-sellers-search"
              className="flex-1 max-w-xs bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-1 text-sm focus:outline-none"
            />
          </div>
          {availableFiltered.length === 0 ? (
            <div className="text-sm text-[#7a6a5a] py-8 text-center">No products match.</div>
          ) : (
            <ul className="divide-y divide-[#e5dfd0] max-h-[520px] overflow-y-auto">
              {availableFiltered.map((p) => (
                <li key={p.id} className="py-3 flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#e9e2d3] overflow-hidden shrink-0">
                    {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-base truncate">{p.name}</div>
                    <div className="text-[10px] tracking-[0.22em] text-[#7a6a5a] uppercase">{p.shade} · {p.category}</div>
                  </div>
                  <div className="text-sm text-[#5a4a3e] whitespace-nowrap">Rs. {p.price?.toFixed?.(2) ?? p.price}</div>
                  <button
                    onClick={() => add(p.id)}
                    data-testid={`add-catalog-${p.id}`}
                    className="inline-flex items-center gap-1 border border-[#1a1410] px-3 py-1 text-[10px] tracking-[0.22em] hover:bg-[#1a1410] hover:text-[#f5f1e6]"
                  >
                    <Check className="w-3 h-3" strokeWidth={1.8} /> ADD
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default BestSellersTab;
